import { useState, useEffect, useRef } from "react";
import { SITE_GATE } from "@/constants/testIds";

// Soft gate for the pre-launch site. This is presentation, not security: the
// password ships in the bundle and anyone can read it. It only keeps casual
// visitors out until launch.
const PASSWORD = "soon2026";
// Suffix is bumped whenever PASSWORD changes, so anyone holding an unlock from
// the previous password is asked again rather than staying in on the old one.
const STORAGE_KEY = "verlo_site_unlock_soon2026";

export default function PasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  // While locked: freeze the page behind the overlay and focus the field.
  useEffect(() => {
    if (unlocked) return;

    const lenis = window.__lenis;
    if (lenis) lenis.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();

    return () => {
      if (window.__lenis) window.__lenis.start();
      document.body.style.overflow = prevOverflow;
    };
  }, [unlocked]);

  function handleSubmit(e) {
    e.preventDefault();
    if (value.trim().toLowerCase() !== PASSWORD) {
      setError(true);
      setValue("");
      inputRef.current?.focus();
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // Private browsing — unlock for this page view only.
    }
    setUnlocked(true);
  }

  if (unlocked) return children;

  return (
    <>
      <div
        className="pointer-events-none select-none blur-xl saturate-50"
        aria-hidden="true"
        inert
      >
        {children}
      </div>

      <div
        data-testid={SITE_GATE.overlay}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-verlo/70 px-6"
      >
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm rounded-xl bg-paper px-8 py-10 text-center"
        >
          <img
            src="/verlo-logo-blue.svg"
            alt="Verlo"
            className="mx-auto mb-6 h-8 w-auto"
          />
          <h1 className="font-display text-2xl text-verlo">
            This site isn't live yet
          </h1>
          <p className="mt-2 text-sm text-verlo/60">
            Enter the password to take a look around.
          </p>

          <input
            ref={inputRef}
            data-testid={SITE_GATE.passwordInput}
            type="password"
            autoComplete="off"
            aria-label="Site password"
            aria-invalid={error}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(false);
            }}
            className={`mt-6 w-full rounded-md border bg-white px-4 py-3 text-center text-verlo outline-none transition-colors focus:border-brass ${
              error ? "border-red-500" : "border-verlo/20"
            }`}
          />

          {error && (
            <p
              data-testid={SITE_GATE.error}
              role="alert"
              className="mt-2 text-sm text-red-600"
            >
              That's not it. Try again.
            </p>
          )}

          <button
            data-testid={SITE_GATE.submitButton}
            type="submit"
            className="mt-4 w-full rounded-md bg-verlo px-4 py-3 text-sm font-medium text-paper transition-colors hover:bg-verlo/90"
          >
            Enter
          </button>
        </form>
      </div>
    </>
  );
}
